import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { FeatureStackIcon } from './icons/HowElementsExplorerIcons';

type HierarchyRow = {
  key: string;
  label: string;
  value: string;
  leading: ReactNode;
  pl: number | string;
};

const hierarchyRowReveal = keyframes`
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const hierarchyRows: HierarchyRow[] = [
  {
    key: 'category',
    label: 'Category',
    value: 'Graphics',
    leading: <Box sx={{ bgcolor: 'primary.main', borderRadius: '50%', height: 14, width: 14 }} />,
    pl: 0,
  },
  {
    key: 'element-category',
    label: 'Element Category',
    value: 'Color Features',
    leading: <KeyboardArrowRightIcon sx={{ color: 'text.secondary', fontSize: 14 }} />,
    pl: '75px',
  },
  {
    key: 'element',
    label: 'Element',
    value: 'Background Color',
    leading: <FeatureStackIcon />,
    pl: '160px',
  },
  {
    key: 'tag',
    label: 'Tag',
    value: 'Blue',
    leading: <SellOutlinedIcon sx={{ color: 'text.secondary', fontSize: 12 }} />,
    pl: '240px',
  },
];

export function HierarchyStructureSection(): ReactNode {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          color: 'text.primary',
          fontSize: '16px',
          fontWeight: 600,
        }}
      >
        Understanding the Hierarchy Structure
      </Typography>
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: '14px',
          mt: 1,
        }}
      >
        Elements are organized in a 4-level hierarchy. Each level provides more specific detail:
      </Typography>
      <Box
        sx={{
          backgroundColor: 'background.default',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mt: 3,
          p: 3,
        }}
      >
        {hierarchyRows.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1.5,
              pl: row.pl,
              opacity: 0,
              transform: 'translateY(6px)',
              animation: `${hierarchyRowReveal} 360ms ease-out forwards`,
              animationDelay: `${index * 170}ms`,
            }}
          >
            {row.leading}
            <Typography
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              {row.label}
            </Typography>
            <Box
              sx={{
                background:
                  'linear-gradient(90deg,  #D1D5DC 0%,  #D1D5DC 85%,rgba(209, 213, 220, 0) 100%)',
                flex: 1,
                height: '1px',
              }}
            />
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
